/**
 * MADE-094: WaitingCard
 * Renders an order card that is in `waiting_on_production` state.
 * Appears inside the collapsible "Waiting on Production" sub-section of the Received column.
 *
 * Visual treatment: amber/yellow left border — distinct from:
 *   - Normal KanbanCard (no border)
 *   - BlockedCard (red border — production cards with materials shortages)
 *
 * No action buttons — card auto-transitions to pack_and_ship when production completes.
 */

import React from 'react';
import { Paper, Typography, Stack, Chip, Button, Box } from '@mui/material';
import { AccessTime as ClockIcon } from '@mui/icons-material';
import type { WorkOrder } from '../../types/madebb.types';

// Human-friendly labels for production statuses shown inline
const PRODUCTION_STATUS_LABELS: Record<string, string> = {
  received:      'Received',
  in_production: 'In Production',
  quality_check: 'Quality Check',
  pack_and_ship: 'Pack & Ship',
  completed:     'Completed',
  blocked:       'Blocked',
};

export interface WaitingCardProps {
  workOrder: WorkOrder;
  /** The manufacturing order ID of the production card fulfilling this order */
  linkedProductionOrderId: string | null | undefined;
  /** Current status of the linked production card (e.g. 'in_production') */
  linkedProductionStatus: string | null | undefined;
  onViewDetail: (wo: WorkOrder) => void;
}

export const WaitingCard: React.FC<WaitingCardProps> = ({
  workOrder,
  linkedProductionOrderId,
  linkedProductionStatus,
  onViewDetail,
}) => {
  const productionStatusLabel = linkedProductionStatus
    ? (PRODUCTION_STATUS_LABELS[linkedProductionStatus] ?? linkedProductionStatus.replace(/_/g, ' '))
    : 'Unknown';

  return (
    <Paper
      elevation={1}
      data-testid="waiting-card"
      sx={{
        p: 1.5,
        borderRadius: 2,
        borderLeft: '4px solid #f57c00', // amber — distinct from blocked (red) and normal cards
        cursor: 'pointer',
        bgcolor: '#fffde7',              // very light amber tint
        '&:hover': { bgcolor: '#fff8e1' },
      }}
      onClick={() => onViewDetail(workOrder)}
    >
      {/* Header row */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5}>
        <Typography variant="subtitle2" fontWeight={700} noWrap>
          {workOrder.orderNumber}
        </Typography>
        <Chip
          label="Order"
          size="small"
          sx={{ fontSize: 10, height: 20, bgcolor: '#ef6c00', color: 'white' }}
        />
      </Stack>

      {/* Item name */}
      <Typography variant="body2" noWrap color="text.secondary" mb={0.5}>
        {workOrder.description}
      </Typography>

      {/* Customer */}
      {workOrder.customerName && (
        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
          {workOrder.customerName}
        </Typography>
      )}

      {/* Waiting-on-production indicator */}
      <Stack direction="row" alignItems="flex-start" spacing={0.75} mt={0.5} mb={0.5}>
        <ClockIcon sx={{ fontSize: 16, color: '#f57c00', mt: '2px', flexShrink: 0 }} />
        <Box>
          <Typography variant="caption" fontWeight={700} color="#e65100" display="block">
            Waiting on Production
          </Typography>
          {linkedProductionOrderId ? (
            <Typography variant="caption" color="text.secondary">
              {linkedProductionOrderId}{' '}
              <Box component="span" sx={{ color: '#f57c00', fontWeight: 600 }}>
                ({productionStatusLabel})
              </Box>
            </Typography>
          ) : (
            <Typography variant="caption" color="text.disabled">
              Production card not yet assigned
            </Typography>
          )}
        </Box>
      </Stack>

      {/* View details link — stops propagation so Paper onClick and button onClick don't double-fire */}
      <Button
        size="small"
        variant="text"
        sx={{ mt: 0.5, fontSize: 11, p: 0, color: '#e65100' }}
        onClick={e => { e.stopPropagation(); onViewDetail(workOrder); }}
      >
        View details
      </Button>
    </Paper>
  );
};
